// ABOUTME: Custom hook for resizable sidebar panels with localStorage persistence.
// ABOUTME: Supports both mouse and touch events for desktop and mobile devices.

import { useCallback, useEffect, useState } from "react"

interface UseResizableSidebarProps {
  key: string
  defaultWidth: number
  minWidth: number
  maxWidth: number
  direction: "left" | "right"
}

interface DragState {
  startX: number
  startWidth: number
}

export const useResizableSidebar = ({ key, defaultWidth, minWidth, maxWidth, direction }: UseResizableSidebarProps) => {
  const [width, setWidth] = useState(() => {
    try {
      const savedWidth = localStorage.getItem(key)
      if (savedWidth) {
        return Math.min(Math.max(Number(savedWidth), minWidth), maxWidth)
      }
    } catch {
      // localStorage may be unavailable in some environments (e.g., private browsing)
    }
    return defaultWidth
  })

  const [dragState, setDragState] = useState<DragState | null>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setDragState({
        startX: e.clientX,
        startWidth: width,
      })
      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
    },
    [width],
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return
      setDragState({
        startX: touch.clientX,
        startWidth: width,
      })
      document.body.style.userSelect = "none"
    },
    [width],
  )

  const handleDoubleClick = useCallback(() => {
    setWidth(defaultWidth)
  }, [defaultWidth])

  useEffect(() => {
    if (!dragState) return

    const calculateNewWidth = (clientX: number) => {
      const deltaX = clientX - dragState.startX
      // If direction is 'left': moving right (+) increases width
      // If direction is 'right': moving left (-) increases width, so we invert delta
      const effectiveDelta = direction === "left" ? deltaX : -deltaX
      return Math.min(Math.max(dragState.startWidth + effectiveDelta, minWidth), maxWidth)
    }

    const handleMouseMove = (e: MouseEvent) => {
      setWidth(calculateNewWidth(e.clientX))
    }

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (touch) setWidth(calculateNewWidth(touch.clientX))
    }

    const handleEnd = () => {
      setDragState(null)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleEnd)
    window.addEventListener("touchmove", handleTouchMove)
    window.addEventListener("touchend", handleEnd)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleEnd)
      window.removeEventListener("touchmove", handleTouchMove)
      window.removeEventListener("touchend", handleEnd)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
  }, [dragState, direction, minWidth, maxWidth])

  // Persist width to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(key, String(width))
    } catch {
      // localStorage may be unavailable in some environments (e.g., private browsing)
    }
  }, [width, key])

  return {
    width,
    isResizing: dragState !== null,
    handleMouseDown,
    handleTouchStart,
    handleDoubleClick,
  }
}
